
import { supabase } from '@/integrations/supabase/client';
import { NTBCache } from '@/utils/ntbCache';

export interface NTBArticle {
  id: string;
  title: string;
  summary: string;
  content: string;
  publishedDate: string;
  author?: string;
  category?: string;
}

export class NTBService {
  private static readonly BASE_URL = 'https://nyheter.ntb.no/ntbWeb/api/j1/search/full?search.service=news';

  static async fetchArticles(limit = 10): Promise<NTBArticle[]> {
    const cacheKey = `articles_${limit}`;
    
    try {
      // Check cache first
      const cached = NTBCache.get(cacheKey);
      if (cached) {
        return cached;
      }

      console.log('Fetching NTB articles from API...');
      
      // Use Supabase edge function to handle the API call with Basic Auth
      const { data, error } = await supabase.functions.invoke('fetch-ntb-news', {
        body: { limit }
      });

      if (error) {
        console.error('Error fetching NTB articles:', error);
        // Provide more specific error message based on the error
        let errorMessage = 'Failed to fetch NTB articles';
        if (error.message?.includes('status: 500')) {
          errorMessage = 'NTB API server error. Please check your API credentials or try again later.';
        } else if (error.message?.includes('credentials not configured')) {
          errorMessage = 'NTB API credentials are not configured. Please contact administrator.';
        } else if (error.message?.includes('status: 401') || error.message?.includes('status: 403')) {
          errorMessage = 'Invalid NTB API credentials. Please contact administrator.';
        }
        throw new Error(errorMessage);
      }

      if (!data || !data.articles) {
        console.warn('No articles returned from NTB API');
        return [];
      }

      // Cache the results
      NTBCache.set(cacheKey, data.articles);

      return data.articles;
    } catch (error) {
      console.error('NTBService error:', error);
      throw error;
    }
  }

  static async getArticleById(id: string, searchDate?: string): Promise<{ article: NTBArticle | null, error?: string, notFound?: boolean }> {
    const cacheKey = `article_${id}_${searchDate || 'default'}`;
    
    try {
      // Check cache first
      const cached = NTBCache.get(cacheKey);
      if (cached) {
        return cached;
      }

      console.log('Fetching NTB article from API:', id);

      const { data, error } = await supabase.functions.invoke('fetch-ntb-news', {
        body: { articleId: id, searchDate }
      });

      if (error) {
        console.error('Error fetching single NTB article:', error);
        // Provide more specific error message based on the error
        let errorMessage = 'Failed to fetch article';
        if (error.message?.includes('status: 500')) {
          errorMessage = 'NTB API server error. Please check your API credentials or try again later.';
        } else if (error.message?.includes('credentials not configured')) {
          errorMessage = 'NTB API credentials are not configured. Please contact administrator.';
        } else if (error.message?.includes('status: 401') || error.message?.includes('status: 403')) {
          errorMessage = 'Invalid NTB API credentials. Please contact administrator.';
        }
        const result = { 
          article: null, 
          error: errorMessage
        };
        // Don't cache errors
        return result;
      }

      const result = {
        article: data?.article || null,
        notFound: data?.notFound || false,
        error: data?.notFound ? `Artikkelen med ID ${id} finnes ikke i NTB-arkivet eller er ikke tilgjengelig med din API-nøkkel` : undefined
      };

      // Cache successful results (including not found results to prevent repeated API calls)
      NTBCache.set(cacheKey, result);

      return result;
    } catch (error) {
      console.error('Error getting NTB article by ID:', error);
      return { 
        article: null, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}
